import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../../helpers/download';
import {Watch} from 'vue-property-decorator';
import * as screenfull from 'screenfull';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {downloadCsv, arrayToCsv, Table, Icon, Tooltip, pivotData, formatDate, formatNumber, pivotDataWithAggregation} from 'wc-lib';
import {format} from 'date-fns';
import { url } from 'inspector';
import {NemRegions, FuelTypeMap} from '../config';


require('wc-lib/icons/downloadcircle.svg');
require('wc-lib/icons/fullscreen.svg');
require('wc-lib/icons/electricity.svg');
require('wc-lib/icons/wind-generation.svg');
require('wc-lib/icons/aemo_battery_storage.svg');
require('wc-lib/icons/aemo_coal.svg');
require('wc-lib/icons/aemo_gas.svg');
require('wc-lib/icons/aemo_solar_farm.svg');
require('wc-lib/icons/aemo_hydro.svg');
require('wc-lib/icons/aemo_fuel_oil.svg');
require('wc-lib/icons/aemo_biomass.svg');
require('wc-lib/icons/aemo_solar_rooftop.svg');

@Component({
  template: require('./fuel-mix.html'),
  name: 'fuel-mix',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-tooltip': Tooltip
  }
})
export class NemFuelMix extends Vue {
  isFullscreen = false;
  data: Array<any> = [];
  apiData: Array<any> = [];
  dynamicView: CustomDynamicView<any>;
  regions = [...NemRegions];
  selectedRegion: string = 'ALL';
  selectedInterval: string = 'CURRENT'; 
  currentInterval: any = null;
  intervalRefresh: any;
  id: any = {};
  currentFuelTypes = [];
  totalFuelTypes = [];
  chartConfig: any  = null;
  fuelTypes = [];
  fuelTypeMap = FuelTypeMap;
  showCursor = false;
  chartObject = null;
  chartCursorCallback = null;
  formatNumber = formatNumber;

  getFuelTypeValue(value: number) {
    if (this.selectedInterval === 'CURRENT') {
      return value < 10000 ? 'MW' : 'GW';
    } else {
      return value < 10000 ? 'MWh' : 'GWh';
    }
  }

  get currentIntervalDateTime() {
    if (this.selectedInterval == 'CURRENT' && this.data.length > 0) 
      return '(' + formatDate(this.data[0].DATETIME, 'd MMMM yyyy - HH:mm') + ')';

    return '';
  }

  created() {
    this.regions.push({
      value: 'ALL',
      label: 'NEM'
    });
    if (screenfull.isEnabled) {
      (screenfull as any).onchange(() => {
        this.isFullscreen = (screenfull as any).isFullscreen;
      });
    }
    this.fetchData();
    this.intervalRefresh = setInterval(() => {
      this.fetchData();
    }, 300000);
  }
  
  async fetchData() {
    let result = await DataManager.addEndPoint('../api/report/FUEL', {"type":[this.selectedInterval]}, this.selectedInterval === 'CURRENT' ? 60000 : undefined, `FUEL_${this.selectedInterval}`, undefined, 'yyyy-MM-ddTHH:mm:ss', ['DATETIME']);
    this.id[this.selectedInterval] = result;
    this.dynamicView = DataManager.getDynamicView(this.id[this.selectedInterval], `${this.selectedInterval}-fuel-mix`);
    this.applyFilter();
  }

  @Watch('selectedInterval')
  @Watch('selectedRegion')
  filterChange() {
    this.currentInterval = null;
    this.fetchData();
  } 

  selectRegion(region: string) {
    this.selectedRegion = region;
  }

  applyFilter() {
    let fuelTypes = [];
    this.dynamicView.removeFilters();
    if (this.selectedRegion !== 'ALL')
      this.dynamicView.applyFind({ 'STATE': this.selectedRegion });
    let data = this.dynamicView.data();
    this.apiData = data;
    for (const row of data) {
      if (fuelTypes.indexOf(row.FUEL_TYPE) === -1) {
        fuelTypes.push(row.FUEL_TYPE);
      }
    }

    data = pivotDataWithAggregation(data, ['DATETIME'], 'FUEL_TYPE', 'SUPPLY');
    if (data.length > 0 && !this.currentInterval)
      this.currentInterval = data[data.length - 1].DATETIME;

    this.fuelTypes = fuelTypes.sort();
    this.data = data;
    this.chartConfig = this.getChartConfig();

    if (this.selectedInterval === 'CURRENT' && data.length > 0) {
      this.setCurrentFuelTypes(data[0]);
    }

    this.totalFuelTypes = this.getTotalFuelTypes(data);
  }

  toggleFullscreen() {
    (screenfull as any).toggle();
  }

  download() {
    const columns = [
      {      
        title: 'DateTime',
        field: 'DATETIME',
        formatter: (row) => {
          return formatDate(row.DATETIME, 'dd/MM/yyyy HH:mm');
        }
      },
      {      
        title: 'State',
        field: 'STATE',
        formatter: (row) => {
          return row.STATE.replace('1', '');
        }
      },
      {      
        title: 'Fuel Type',
        field: 'FUEL_TYPE'
      },
      {      
        title: 'Supply',
        field: 'SUPPLY'
      }
    ];
    let csv = arrayToCsv(this.apiData, columns);
    downloadCsv(`NEMFUELMIX_${this.selectedRegion}_${formatDate(new Date(), 'yyyyMMddHHmm')}.csv`, csv);
  }

  getTotalFuelTypes(data : any[]) {
    const totalFuelTypes = {};
    const totalFuelTypesArray = [];
    let total = 0;

    for (const row of data) {
      for (const property in row) {
        if (property === 'DATETIME') continue;

        const value = row[property];
        const title =  property.replace(' SUPPLY', '');

        let fuelTypeMap = this.fuelTypeMap[title];
        if (!fuelTypeMap) {
          fuelTypeMap = this.fuelTypeMap.default;
        }

        if (totalFuelTypes[title]) {
          totalFuelTypes[title].value += value;
        } else {
          totalFuelTypes[title] = {
            title: title,
            value: value,
            icon: fuelTypeMap.icon,
            colour: fuelTypeMap.colour,
            border: fuelTypeMap.border
          };
        }
        total += value;
      }
    }

    for (const fuelType in totalFuelTypes) {
      const totalFuelType = totalFuelTypes[fuelType];
      totalFuelType.percentage = totalFuelType.value / total;
      totalFuelTypesArray.push(totalFuelType);
    }


    totalFuelTypesArray.sort((a,b) => {
      if (a.title < b.title) {
        return -1;
      }
      if (a.title > b.title) {
        return 1;
      }
      return 0;
    });
    return totalFuelTypesArray;
  }

  setCurrentFuelTypes(row: any) {
    this.currentFuelTypes = this.getTotalFuelTypes([row]);
  }

  chartRendered(chart: any) {
    this.chartObject = chart;
    this.chartCursorCallback = this.setChartCursor.bind(this);
    this.setChartCursor({chart: chart});
    chart.addListener('drawn', this.chartCursorCallback);
  }

  setChartCursor(config: any) {
    if (this.$refs.mixByTime && (this.$refs.mixByTime as HTMLElement).offsetHeight > 0) {
      if (config.chart.chartCursor.showCursorAt)
        config.chart.chartCursor.showCursorAt(this.currentInterval);
    }
  }

  getChartConfig() {
    if ((this.chartObject) && (this.chartObject.listenersAdded))
      this.chartObject.removeListener(this.chartObject, 'drawn', this.chartCursorCallback);

    this.chartObject = null;
    const graphs = [];

    for (const fuelType of this.fuelTypes) {
      let fuelTypeMap = this.fuelTypeMap[fuelType];
      if (!fuelTypeMap) {
        fuelTypeMap = this.fuelTypeMap.default;
      }

      graphs.push({
        valueAxis: "v1", 
        valueField: fuelType + ' SUPPLY', 
        lineColor: fuelTypeMap.border, 
        lineThickness: 1, 
        fillAlphas: 1,
        fillColors: [fuelTypeMap.colour],
        showBalloon: false
      });
    }
    return {
      type: 'serial',
      marginRight: 0,
      marginLeft: 0,
      marginTop: 1,
      marginBottom: 0,
      addClassNames: true,
      panEventsEnabled: false,
      numberFormatter: {
        precision: 2, 
        decimalSeparator: '.', 
        thousandsSeparator: ','
      },
      valueAxes: [{
        id: "v1",
        position: "right",
        labelsEnabled: false,
        gridAlpha: 0,
        axisAlpha: 0,
        stackType: "regular",
        ignoreAxisWidth: true
      }],
      graphs: graphs,
      categoryAxis: {
        parseDates: true,
        //labelRotation: 45,
        minPeriod: "mm",
        color: '#8ea9a9',
        gridAlpha: 0,
        labelsEnabled: true,
        axisAlpha: 0
      },
      categoryField: "DATETIME",
      chartCursor: {
        zoomable: false,
        bulletsEnabled: false,
        categoryBalloonEnabled: true,
        cursorColor: '#000000',
        leaveCursor: true,
        categoryBalloonFunction: (value) => {
          this.currentInterval = value;
          const rowIndex = this.data.findIndex(x => x.DATETIME.getTime() === value.getTime());
          const row = this.data[rowIndex];
          const previousRow = rowIndex > 0 ? this.data[rowIndex-1] : row;

          this.setCurrentFuelTypes(row);

          const previousDateTime = rowIndex > 0 ? formatDate(previousRow.DATETIME, this.selectedInterval === '3M' || this.selectedInterval === '12M' ? "dd MMM - " : "HH:mm - ") : '';
          return previousDateTime + formatDate(value, 
            this.selectedInterval === '3M' || this.selectedInterval === '12M' ? "dd MMM yyyy" : "HH:mm dd MMM");
        }
      }
    };
  }

  destroyed() {
    clearInterval(this.intervalRefresh);
  }
}


Vue.filter('formatSupplyNumber', (value: any) => {
  if (value < 10000) {
    return formatNumber(value, '0,00');
  } else {
    return formatNumber(value / 1000, '0,00.0');
  }
});



// WEBPACK FOOTER //
// src/views/electricity/nem/fuel-mix.ts